import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useCMS } from '../context/CMSContext';
import {
    loadKwikFormsSdk,
    buildHiddenCustomFields,
    listenForFormEvents,
    pushToDataLayer,
} from '../lib/kwikforms';
import StickyFooter from '../components/StickyFooter';
import StickyCTAs from '../components/StickyCTAs';

// Same below-fold chunking as DefaultTemplate — the form paints first.
const TestDetails = lazy(() => import('../components/TestDetails'));
const MostBookedPackages = lazy(() => import('../components/MostBookedPackages'));
const WhyBookWithUs = lazy(() => import('../components/WhyBookWithUs'));
const TestimonialsSection = lazy(() => import('../components/TestimonialsSection'));
const CantFindSection = lazy(() => import('../components/CantFindSection'));
const FAQSection = lazy(() => import('../components/FAQSection'));
const Footer = lazy(() => import('../components/Footer'));

const SectionPlaceholder = () => <div className="min-h-[320px]" aria-hidden="true" />;

// Generous because the iframe only appears after the SDK's own config round
// trips; a slow mobile connection must not be reported as a failure.
const RENDER_TIMEOUT_MS = 12000;

const MOBILE_BREAKPOINT_QUERY = '(max-width: 1023px)';

// DefaultTemplate with one substitution: HeroSection's name/phone/city form is
// replaced by the KwikForms iframe. The hero markup is reproduced here rather
// than adding a prop to HeroSection, because HeroSection renders its form twice
// (once per breakpoint) and two elements sharing one container id is what stops
// the SDK rendering. A single container placed with CSS grid gives the same
// mobile sequence — banner, form, USPs — with one node.
const KwikFormTemplate = () => {
    const { data } = useCMS();
    const { kwikForm, hero } = data;

    const isFormEnabled = Boolean(kwikForm?.enabled);
    const containerId = kwikForm?.containerId || 'kf-form';
    const desktopMinHeight = kwikForm?.minHeight || '350';
    const desktopMaxHeight = kwikForm?.maxHeight || '550';

    const [isMobileViewport, setIsMobileViewport] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches,
    );

    useEffect(() => {
        const media = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
        const sync = () => setIsMobileViewport(media.matches);
        media.addEventListener('change', sync);
        return () => media.removeEventListener('change', sync);
    }, []);

    const minHeight = isMobileViewport ? (kwikForm?.minHeightMobile || desktopMinHeight) : desktopMinHeight;
    const maxHeight = isMobileViewport ? (kwikForm?.maxHeightMobile || desktopMaxHeight) : desktopMaxHeight;

    // A disabled form leaves nothing to show a customer, so in production the
    // whole column is dropped and the banner takes the full width. Development
    // keeps it so the "not enabled" hint has somewhere to appear.
    const showFormColumn = isFormEnabled || import.meta.env.DEV;

    const containerRef = useRef(null);
    const [hasFailed, setHasFailed] = useState(false);

    useEffect(() => {
        if (!isFormEnabled) return undefined;

        const container = containerRef.current;
        if (!container) return undefined;

        let isActive = true;
        let renderCheckTimer = null;

        const markFailed = (reason) => {
            if (!isActive) return;
            console.error('[KwikForms]', reason);
            setHasFailed(true);
        };

        // Documented GoKwik event: fired when KwikPass reports itself unhealthy,
        // e.g. its config request being refused with a 403.
        const handleHealth = (event) => {
            if (event.detail?.status === false) {
                markFailed('KwikPass reported itself unhealthy (kp-health)');
            }
        };
        window.addEventListener('kp-health', handleHealth);

        // Form start / error / submitted, forwarded to GTM as events that carry no
        // personal data. Analytics only: the webhook is what records the lead.
        const stopListeningForFormEvents = listenForFormEvents(pushToDataLayer);

        loadKwikFormsSdk()
            .then((sdk) => {
                if (!isActive) return;

                sdk.kwikForm({
                    containerId,
                    status: true,
                    minHeight,
                    maxHeight,
                    customFieldValues: buildHiddenCustomFields(),
                });

                // Some rejections raise no event at all — the SDK just never draws
                // anything, leaving a blank card. The iframe is located by its title
                // (set by the SDK) rather than inside our container, so this stays
                // correct wherever the SDK chooses to attach it.
                renderCheckTimer = setTimeout(() => {
                    if (!document.querySelector('iframe[title="Kwik Form"]')) {
                        markFailed(`KwikForm iframe did not appear within ${RENDER_TIMEOUT_MS / 1000}s`);
                    }
                }, RENDER_TIMEOUT_MS);
            })
            .catch((error) => markFailed(error.message));

        return () => {
            isActive = false;
            clearTimeout(renderCheckTimer);
            window.removeEventListener('kp-health', handleHealth);
            stopListeningForFormEvents();
            // The SDK exposes no teardown call, so emptying the container is the
            // only way to stop a stale iframe surviving a route change and
            // colliding with the next mount's kwikForm() call.
            container.replaceChildren();
        };
    }, [isFormEnabled, containerId, minHeight, maxHeight]);

    return (
        <div className="min-h-screen bg-white overflow-x-hidden">
            <section className="bg-[#f2f4f7] pt-2 pb-4 lg:py-10">
                <div className="container mx-auto px-4">
                    {/* One column on mobile, so the DOM order (banner, form, USPs) is the
                        visual order. From lg up, explicit grid placement puts the banner
                        top-left, the form on the right spanning both rows, and the USPs
                        under the banner. minmax(0, ...) stops a wide child from stretching
                        its column past 65/35. */}
                    <div
                        className={`grid grid-cols-1 gap-3 lg:gap-6 items-start ${
                            showFormColumn
                                ? 'lg:grid-cols-[minmax(0,65fr)_minmax(0,35fr)]'
                                : 'lg:grid-cols-1'
                        }`}
                    >

                        <div className="lg:col-start-1 lg:row-start-1">
                            <div className="relative rounded-xl lg:rounded-2xl overflow-hidden shadow-sm bg-white">
                                {hero?.showHeroContent !== false && (
                                    <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-12 z-10 bg-gradient-to-r from-black/60 via-black/40 to-transparent">
                                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 lg:mb-5 drop-shadow-md leading-tight max-w-lg">
                                            {(hero?.heroHeading || 'Your Health, {Our Priority}')
                                                .split(/(\{.*?\})/g)
                                                .map((part, index) => (
                                                    part.startsWith('{') && part.endsWith('}')
                                                        ? <span key={index} className="text-green-400">{part.slice(1, -1)}</span>
                                                        : part
                                                ))}
                                        </h1>
                                        <p className="text-sm md:text-base lg:text-lg text-gray-100 max-w-md drop-shadow-md leading-relaxed">
                                            {hero?.heroDescription}
                                        </p>
                                    </div>
                                )}

                                <img
                                    src={hero?.mobileBanner}
                                    alt="Banner"
                                    fetchPriority="high"
                                    decoding="async"
                                    className="w-full h-auto min-h-[350px] object-cover lg:hidden"
                                />
                                <img
                                    src={hero?.desktopBanner}
                                    alt="Banner"
                                    fetchPriority="high"
                                    decoding="async"
                                    className="w-full h-auto min-h-[400px] object-cover hidden lg:block"
                                />
                            </div>
                        </div>

                        {showFormColumn && (
                            <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
                                <div
                                    // Book Now and the sticky CTAs scroll to these ids. On
                                    // every other template they belong to the hero form's
                                    // name input; here the KwikForm is the form, so the
                                    // buttons should land on it rather than do nothing.
                                    id="hero-name-input-desktop"
                                    className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 p-4 lg:p-5"
                                >
                                    <h2 id="hero-name-input-mobile" className="text-xl font-bold text-slate-800 mb-3">
                                        {hero?.formTitle || 'Book Your Test Today'}
                                    </h2>

                                    {isFormEnabled ? (
                                        <>
                                            {/* GoKwik's own iframe sets overscroll-behavior: contain, which
                                                stops an unfinished internal scroll from handing the gesture on
                                                to the page — a swipe that starts on the iframe does nothing once
                                                its own content can't scroll further, instead of scrolling the
                                                page. auto is the browser default; this only exists to win back
                                                that one property from their stylesheet. The id selector beats
                                                their class selector on specificity, so this applies regardless
                                                of which stylesheet loads first. */}
                                            <style>{`#${containerId} iframe { overscroll-behavior: auto; touch-action: pan-y; }`}</style>
                                            <div
                                                ref={containerRef}
                                                id={containerId}
                                                style={{ minHeight: `${minHeight}px` }}
                                                className="w-full"
                                            />
                                            {hasFailed && (
                                                <p className="text-sm text-gray-600 text-center">
                                                    The form could not be loaded. Please refresh the
                                                    page or call us to book.
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-sm text-gray-500 text-center py-6">
                                            Not enabled — tick &quot;Show the Kwik Form on this
                                            page&quot; in admin Form Settings.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {hero?.usps?.length > 0 && (
                            <div className="text-center lg:col-start-1 lg:row-start-2">
                                <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 font-sans">
                                    {hero.uspsTitle || 'Why Choose Curelo Health?'}
                                </h2>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-2">
                                    {hero.usps.map((point, index) => (
                                        <div key={index} className="flex flex-col items-center text-center gap-3">
                                            <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center border border-gray-100 p-3">
                                                <img src={point.icon} alt={point.title} className="w-full h-full object-contain" />
                                            </div>
                                            <h3 className="text-xs lg:text-sm font-semibold text-gray-800 leading-tight max-w-[140px]">
                                                {point.title}
                                            </h3>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </section>

            <Suspense fallback={<SectionPlaceholder />}>
                <TestDetails />
                <MostBookedPackages />
                <WhyBookWithUs />
                <TestimonialsSection />
                <CantFindSection />
                <FAQSection />
                <Footer />
            </Suspense>

            <StickyFooter />
            <StickyCTAs />
        </div>
    );
};

export default KwikFormTemplate;
